import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link, Navigate } from "react-router-dom";
import { toast } from "react-toastify";
import { signInWithEmailAndPassword, onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../../config/firebase/firebaseConfig";

import logoLight from "assets/images/logo-light.png";
import logoDark from "assets/images/logo-dark.png";

const Login = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState<{email?: string, password?: string}>({});
    const [currentUser, setCurrentUser] = useState(null);
    const [userLoggedIn, setUserLoggedIn] = useState(false);
    const [authLoading, setAuthLoading] = useState(true);
    const [isLoggingIn, setIsLoggingIn] = useState(false);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const defaultFormData = {
        email: "",
        password: "",
    };

    const [formData, setFormData] = useState(defaultFormData);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setCurrentUser(user);
            setUserLoggedIn(!!user);
            setAuthLoading(false);
        });

        return () => unsubscribe();
    }, []);

    useEffect(() => {
        const bodyElement = document.body;

        bodyElement.classList.add('flex', 'items-center', 'justify-center', 'min-h-screen', 'py-16', 'bg-cover', 'bg-auth-pattern', 'dark:bg-auth-pattern-dark', 'dark:text-zink-100', 'font-public');

        return () => {
            bodyElement.classList.remove('flex', 'items-center', 'justify-center', 'min-h-screen', 'py-16', 'bg-cover', 'bg-auth-pattern', 'dark:bg-auth-pattern-dark', 'dark:text-zink-100', 'font-public');
        };
    }, []);

    document.title = "Sign In | Lyfe Admin Panel";

    if (authLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-custom-500"></div>
            </div>
        );
    }

    if (userLoggedIn && currentUser && !isLoggingIn) {
        const from = location.state?.from?.pathname || "/dashboard";
        return <Navigate to={from} replace />;
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setErrors((prev) => ({ ...prev, [e.target.name]: undefined }));
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const validateEmail = (email: string) => {
        if (!email.trim()) {
            setErrors((prev) => ({ ...prev, email: "Email is required" }));
            return false;
        } else if (!emailRegex.test(email.trim())) {
            setErrors((prev) => ({ ...prev, email: "Invalid email address" }));
            return false;
        } else {
            setErrors((prev) => ({ ...prev, email: undefined }));
            return true;
        }
    };

    const validatePassword = (password: string) => {
        if (!password.trim()) {
            setErrors((prev) => ({
                ...prev,
                password: "Password is required",
            }));
            return false;
        } else if (password.trim().length < 4) {
            setErrors((prev) => ({
                ...prev,
                password: "Password must be at least 4 characters long",
            }));
            return false;
        } else {
            setErrors((prev) => ({ ...prev, password: undefined }));
            return true;
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const isEmailValid = validateEmail(formData.email);
        const isPasswordValid = validatePassword(formData.password);

        if (!isEmailValid || !isPasswordValid) {
            return;
        }

        try {
            setIsLoading(true);
            setIsLoggingIn(true); // Prevent automatic redirect

            const userCredential = await signInWithEmailAndPassword(
                auth,
                formData.email.trim(),
                formData.password.trim()
            );

            const adminDocRef = doc(db, "admins", userCredential.user.uid);
            const adminDocSnap = await getDoc(adminDocRef);

            if (adminDocSnap.exists()) {
                toast.success("Login successful!");
                setFormData(defaultFormData);

                setCurrentUser(userCredential.user);
                setUserLoggedIn(true);

                const from = location.state?.from?.pathname || "/dashboard";
                navigate(from, { replace: true });
            } else {
                await auth.signOut();
                toast.error("Access denied. You are not authorized to access the admin panel.");

                setCurrentUser(null);
                setUserLoggedIn(false);
            }

        } catch (err: any) {
            console.error("Authentication error:", err);

            if (
                err.code === "auth/invalid-credential" ||
                err.code === "auth/wrong-password" ||
                err.code === "auth/user-not-found" ||
                err.code === "auth/invalid-email"
            ) {
                toast.error("Invalid email or password. Please try again.");
            } else if (err.code === "auth/network-request-failed") {
                toast.error("Network error. Please check your internet connection.");
            } else if (err.code === "auth/too-many-requests") {
                toast.error("Too many failed attempts. Please try again later.");
            } else if (err.code === "auth/user-disabled") {
                toast.error("This account has been disabled. Please contact support.");
            } else {
                toast.error("Sign-in failed. Please try again.");
            }
        } finally {
            setIsLoading(false);
            setIsLoggingIn(false); // Re-enable automatic checks
        }
    };

    return (
        <React.Fragment>
            <div className="mb-0 border-none lg:w-[500px] card bg-white/70 shadow-none dark:bg-zink-500/70">
                <div className="!px-10 !py-12 card-body">
                    <Link to="/">
                        <img src={logoLight} alt="" className="hidden h-6 mx-auto dark:block" />
                        <img src={logoDark} alt="" className="block h-6 mx-auto dark:hidden" />
                    </Link>

                    <div className="mt-8 text-center">
                        <h4 className="mb-2 text-purple-500 dark:text-purple-500">Welcome Back!</h4>
                        <p className="text-slate-500 dark:text-zink-200">Sign in to continue to LyfeAdmin.</p>
                    </div>

                    <form className="mt-10" onSubmit={handleSubmit}>
                        <div className="mb-3">
                            <label htmlFor="email" className="inline-block mb-2 text-base font-medium">Email Address</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                className="form-input dark:bg-zink-600/50 border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                placeholder="Enter your email"
                                onChange={handleChange}
                                onBlur={() => validateEmail(formData.email)}
                                value={formData.email}
                                disabled={isLoading}
                            />
                            {errors.email && (
                                <div className="mt-1 text-sm text-red-500">{errors.email}</div>
                            )}
                        </div>

                        <div className="mb-3">
                            <label htmlFor="password" className="inline-block mb-2 text-base font-medium">Password</label>
                            <input
                                type="password"
                                id="password"
                                name="password"
                                className="form-input dark:bg-zink-600/50 border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                placeholder="Enter your password"
                                onChange={handleChange}
                                onBlur={() => validatePassword(formData.password)}
                                value={formData.password}
                                disabled={isLoading}
                            />
                            {errors.password && (
                                <div className="mt-1 text-sm text-red-500">{errors.password}</div>
                            )}
                        </div>

                        <div className="flex justify-end mb-6">
                            <Link to="/reset-password" className="text-sm text-custom-500 hover:text-custom-600 hover:underline">
                                Forgot Password?
                            </Link>
                        </div>

                        <div className="mt-10">
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="w-full text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isLoading ? (
                                    <div className="flex items-center justify-center">
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                        Signing In...
                                    </div>
                                ) : (
                                    "Sign In"
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </React.Fragment>
    );
};

export default Login;